import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

const services = [
  {
    id: 1,
    icon: '☉',
    title: 'ஜாதக கணிப்பு',
    subtitle: 'Birth Chart Reading',
    description: 'உங்கள் பிறந்த நேரம், தேதி மற்றும் இடத்தின் அடிப்படையில் முழுமையான ஜாதக கணிப்பு.',
    price: '₹500',
    features: ['கிரக நிலை ஆய்வு', 'ராசி பலன்', 'தசா புத்தி கணிப்பு', 'பரிகார ஆலோசனை'],
  },
  {
    id: 2,
    icon: '♀',
    title: 'திருமண பொருத்தம்',
    subtitle: 'Marriage Matching',
    description: 'திருமண பொருத்தம் பார்த்து, இருவரின் ஜாதகங்களை ஒப்பிட்டு ஆலோசனை வழங்குதல்.',
    price: '₹750',
    features: ['10 பொருத்தம் ஆய்வு', 'குண பொருத்தம்', 'தோஷ பரிசீலனை', 'திருமண நல்ல நேரம்'],
  },
  {
    id: 3,
    icon: '♃',
    title: 'தொழில் ஆலோசனை',
    subtitle: 'Career Guidance',
    description: 'உங்கள் ஜாதகத்தின் அடிப்படையில் தொழில் மற்றும் வேலை வாய்ப்பு ஆலோசனை.',
    price: '₹600',
    features: ['தொழில் யோகம்', 'வேலை மாற்றம்', 'சொந்த தொழில்', 'வெற்றி காலம்'],
  },
  {
    id: 4,
    icon: '☽',
    title: 'ராசி பலன்',
    subtitle: 'Rashi Prediction',
    description: 'தினசரி, வாராந்திர மற்றும் மாதாந்திர ராசி பலன் கணிப்பு.',
    price: '₹300',
    features: ['தினசரி பலன்', 'சந்திர ராசி', 'நட்சத்திர பலன்', 'யோக நாட்கள்'],
  },
  {
    id: 5,
    icon: '♄',
    title: 'கிரக தோஷ பரிகாரம்',
    subtitle: 'Dosha Remedies',
    description: 'செவ்வாய் தோஷம், சனி தோஷம் உள்ளிட்ட அனைத்து கிரக தோஷங்களுக்கும் பரிகாரம்.',
    price: '₹400',
    features: ['தோஷ கண்டறிதல்', 'பரிகார பூஜை', 'ரத்தின ஆலோசனை', 'மந்திர உபதேசம்'],
  },
  {
    id: 6,
    icon: '☊',
    title: 'வருட ராசி பலன்',
    subtitle: 'Yearly Forecast',
    description: 'வரும் ஆண்டிற்கான முழுமையான ராசி பலன் மற்றும் முக்கிய நிகழ்வுகள் கணிப்பு.',
    price: '₹800',
    features: ['12 மாத பலன்', 'முக்கிய தேதிகள்', 'கிரக பெயர்ச்சி', 'சிறப்பு பலன்கள்'],
  },
];

const ServicesPage = () => {
  return (
    <motion.div
      className="relative min-h-screen px-4 py-32"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Page Header */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <h1
          className="text-4xl md:text-6xl font-display tracking-wider mb-4"
          style={{
            color: '#d4af37',
            textShadow: '0 0 40px rgba(212, 175, 55, 0.4)',
          }}
        >
          எங்கள் சேவைகள்
        </h1>
        <p
          className="text-xl font-body mb-2"
          style={{ color: 'rgba(212, 175, 55, 0.7)' }}
        >
          Our Services
        </p>
        <p
          className="text-lg md:text-xl font-body italic max-w-2xl mx-auto"
          style={{ color: 'rgba(192, 192, 192, 0.8)' }}
        >
          ஜோதிட ஆலோசனைக்கு இப்போதே தொடர்பு கொள்ளுங்கள்
        </p>
      </motion.div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, index) => (
          <ServiceCard key={service.id} service={service} index={index} />
        ))}
      </div>
    </motion.div>
  );
};

const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;

    const handleMouseEnter = () => {
      gsap.to(card, {
        y: -10,
        scale: 1.02,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(212, 175, 55, 0.2)',
        duration: 0.4,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        y: 0,
        scale: 1,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.1)',
        duration: 0.4,
        ease: 'power2.out',
      });
    };

    card.addEventListener('mouseenter', handleMouseEnter);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mouseenter', handleMouseEnter);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <motion.div
      ref={cardRef}
      className="relative rounded-2xl overflow-hidden cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.1)',
      }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="p-8">
        {/* Icon */}
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
          style={{
            background: 'radial-gradient(circle, rgba(212, 175, 55, 0.15) 0%, transparent 70%)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
          }}
        >
          <span className="text-3xl" style={{ color: '#d4af37' }}>{service.icon}</span>
        </div>

        {/* Title & Price */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3
              className="text-xl font-display tracking-wide"
              style={{ color: '#d4af37' }}
            >
              {service.title}
            </h3>
            <p
              className="text-sm font-body"
              style={{ color: 'rgba(192, 192, 192, 0.6)' }}
            >
              {service.subtitle}
            </p>
          </div>
          <span
            className="text-lg font-display"
            style={{ color: 'rgba(212, 175, 55, 0.8)' }}
          >
            {service.price}
          </span>
        </div>

        {/* Description */}
        <p
          className="font-body text-base mb-6 leading-relaxed"
          style={{ color: 'rgba(192, 192, 192, 0.8)' }}
        >
          {service.description}
        </p>

        {/* Features */}
        <ul className="space-y-2">
          {service.features.map((feature, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-sm font-body"
              style={{ color: 'rgba(192, 192, 192, 0.6)' }}
            >
              <span style={{ color: '#d4af37' }}>✦</span>
              {feature}
            </li>
          ))}
        </ul>

        {/* Book Button */}
        <motion.button
          className="w-full mt-8 py-3 rounded-lg font-display text-sm tracking-wider"
          style={{
            background: 'transparent',
            border: '1px solid rgba(212, 175, 55, 0.4)',
            color: '#d4af37',
          }}
          whileHover={{
            background: 'rgba(212, 175, 55, 0.1)',
            borderColor: 'rgba(212, 175, 55, 0.6)',
            boxShadow: '0 0 20px rgba(212, 175, 55, 0.2)',
          }}
          transition={{ duration: 0.3 }}
        >
          முன்பதிவு செய்யவும்
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ServicesPage;
